import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE, formatApiError } from "../lib/api";
import { downloadCsv } from "../lib/downloadCsv";
import { useAuth } from "../contexts/AuthContext";
import SubstationCombobox from "../components/SubstationCombobox";
import {
  BarChart3,
  Calendar,
  Download,
  RefreshCw,
  ChevronLeft,
  Building2,
  Users2,
  Activity,
  Package,
  AlertTriangle,
} from "lucide-react";
import { formatLocalDate } from "../lib/constants";

const REPORT_DEFS = [
  {
    key: "daily-yard-summary",
    title: "Daily Yard Summary",
    icon: Activity,
    subtitle: "Per-day received vs. completed shipments and net yard inflow.",
    columns: [
      { key: "day", label: "Day" },
      { key: "received", label: "Received" },
      { key: "completed", label: "Completed" },
      { key: "in_yard", label: "Still in yard" },
      { key: "urgent", label: "Urgent" },
      { key: "net_inflow", label: "Net inflow" },
    ],
    supportsDate: true,
    supportsStation: true,
  },
  {
    key: "queue-performance",
    title: "Queue Performance",
    icon: BarChart3,
    subtitle: "Backlog and dwell-time per Runner-Queue stage.",
    columns: [
      { key: "stage", label: "Stage" },
      { key: "count", label: "Count" },
      { key: "median_hours", label: "Median (hrs)" },
      { key: "p95_hours", label: "P95 (hrs)" },
      { key: "max_hours", label: "Max (hrs)" },
    ],
    supportsDate: true,
    supportsStation: true,
  },
  {
    key: "crew-leader-performance",
    title: "Crew Leader Performance",
    icon: Users2,
    subtitle: "Per-leader assigned, open, completed, urgent + completion %.",
    columns: [
      { key: "crew_leader", label: "Crew leader" },
      { key: "total_assigned", label: "Assigned" },
      { key: "open", label: "Open" },
      { key: "completed", label: "Completed" },
      { key: "completion_pct", label: "Completion %" },
      { key: "urgent_open", label: "Urgent open" },
      { key: "ready_for_handover", label: "Ready to hand" },
    ],
    supportsDate: true,
    supportsStation: true,
  },
  {
    key: "project-station-summary",
    title: "Project / Station Summary",
    icon: Building2,
    subtitle: "Volume + stage breakdown for each substation in the window.",
    columns: [
      { key: "project_station", label: "Project / Station" },
      { key: "received", label: "Received" },
      { key: "in_yard", label: "In yard" },
      { key: "at_station", label: "At station" },
      { key: "ready_for_handover", label: "Ready" },
      { key: "completed", label: "Completed" },
      { key: "urgent", label: "Urgent" },
    ],
    supportsDate: true,
    supportsStation: true,
  },
  {
    key: "low-stock-reorder",
    title: "Low Stock & Reorder Report",
    icon: Package,
    subtitle: "Inventory items at or below low-threshold + reorder qty suggestion.",
    columns: [
      { key: "item_name", label: "Item" },
      { key: "item_type", label: "Type" },
      { key: "stock_level", label: "Level" },
      { key: "current_stock", label: "Stock" },
      { key: "low_threshold", label: "Low ≤" },
      { key: "reorder_point", label: "Reorder ≤" },
      { key: "suggested_reorder_qty", label: "Suggested qty" },
      { key: "location_in_yard", label: "Location" },
    ],
    supportsDate: false,
    supportsStation: false,
  },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function Reports() {
  const { isPrivileged, role } = useAuth();
  const nav = useNavigate();
  const allowed = isPrivileged || role === "runner";
  const [start, setStart] = useState(daysAgoIso(30));
  const [end, setEnd] = useState(todayIso());
  const [station, setStation] = useState("");
  const [reload, setReload] = useState(0);

  const sharedFilters = useMemo(() => ({ start, end, project_station: station }), [start, end, station]);

  useEffect(() => {
    if (!allowed) {
      nav("/", { replace: true });
    }
  }, [allowed, nav]);

  if (!allowed) return null;
  const refreshAll = () => setReload((n) => n + 1);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <button
        onClick={() => nav(-1)}
        data-testid="back-btn"
        className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 mb-3"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Admin / Reports
          </div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase text-slate-900 tracking-tight">
            Operational Reports
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Five live reports — filter by date and station, export to CSV.
          </p>
        </div>
        <button
          onClick={refreshAll}
          data-testid="reports-refresh-all"
          className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white"
        >
          <RefreshCw className="h-4 w-4" /> Refresh all
        </button>
      </div>

      {/* Shared filter bar */}
      <div className="rg-card p-4 mb-5 sticky top-[64px] z-30 bg-white border-2 border-slate-200" data-testid="reports-filter-bar">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Start
            </label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              data-testid="reports-start-date"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-slate-900 outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> End
            </label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              data-testid="reports-end-date"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-slate-900 outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> Project / Station
            </label>
            <SubstationCombobox
              value={station}
              onChange={setStation}
              testid="reports-station"
              placeholder="All stations"
            />
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-2 items-center">
          <span className="text-[11px] text-slate-500">Quick range:</span>
          <button
            type="button"
            onClick={() => { setStart(todayIso()); setEnd(todayIso()); }}
            data-testid="reports-quick-today"
            className="text-[11px] font-bold uppercase text-slate-700 hover:text-slate-900 underline"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => { setStart(daysAgoIso(7)); setEnd(todayIso()); }}
            data-testid="reports-quick-7d"
            className="text-[11px] font-bold uppercase text-slate-700 hover:text-slate-900 underline"
          >
            Last 7d
          </button>
          <button
            type="button"
            onClick={() => { setStart(daysAgoIso(30)); setEnd(todayIso()); }}
            data-testid="reports-quick-30d"
            className="text-[11px] font-bold uppercase text-slate-700 hover:text-slate-900 underline"
          >
            Last 30d
          </button>
          <button
            type="button"
            onClick={() => { setStart(daysAgoIso(90)); setEnd(todayIso()); }}
            data-testid="reports-quick-90d"
            className="text-[11px] font-bold uppercase text-slate-700 hover:text-slate-900 underline"
          >
            Last 90d
          </button>
          {station && (
            <button
              type="button"
              onClick={() => setStation("")}
              data-testid="reports-clear-station"
              className="ml-auto text-[11px] font-bold uppercase text-rose-700 hover:text-rose-900 underline"
            >
              Clear station filter
            </button>
          )}
        </div>
      </div>

      <div className="space-y-5">
        {REPORT_DEFS.map((def) => (
          <ReportSection
            key={def.key}
            def={def}
            filters={sharedFilters}
            reloadToken={reload}
          />
        ))}
      </div>
    </div>
  );
}

function ReportSection({ def, filters, reloadToken }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const params = {};
  if (def.supportsDate) {
    if (filters.start) params.start = filters.start;
    if (filters.end) params.end = filters.end;
  }
  if (def.supportsStation && filters.project_station) {
    params.project_station = filters.project_station;
  }

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError("");
    api.get(`/reports/${def.key}`, { params })
      .then(({ data }) => {
        if (cancel) return;
        setRows(data.rows || []);
        setMeta(data || {});
      })
      .catch((e) => {
        if (cancel) return;
        setError(formatApiError(e.response?.data?.detail) || "Failed to load");
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def.key, filters.start, filters.end, filters.project_station, reloadToken]);

  const exportCsv = async () => {
    try {
      await downloadCsv({
        url: `/reports/${def.key}`,
        params: { ...params, format: "csv" },
        fallbackFilename: `${def.key}.csv`,
      });
    } catch (e) {
      // downloadCsv already toasts on failure; log so devs/monitoring see it.
      console.error(`Report CSV export failed (${def.key})`, e);
    }
  };

  const Icon = def.icon;
  const STOCK_PILL = {
    red: "bg-rose-100 text-rose-800 border-rose-300",
    yellow: "bg-amber-100 text-amber-800 border-amber-300",
    green: "bg-emerald-100 text-emerald-800 border-emerald-300",
  };
  const STAGE_PILL = {
    "Pending Preparation": "bg-amber-100 text-amber-800 border-amber-300",
    "Preparing": "bg-blue-100 text-blue-800 border-blue-300",
    "Ready for Handover": "bg-indigo-100 text-indigo-800 border-indigo-300",
    "Completed": "bg-emerald-100 text-emerald-800 border-emerald-300",
    "Not Needed": "bg-slate-100 text-slate-700 border-slate-300",
  };

  return (
    <section className="rg-card p-4 sm:p-5" data-testid={`report-section-${def.key}`}>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="h-10 w-10 grid place-items-center bg-slate-900 text-amber-400 rounded-sm flex-shrink-0">
            <Icon strokeWidth={2.5} className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-lg uppercase text-slate-900 leading-tight">{def.title}</h2>
            <p className="text-xs text-slate-600">{def.subtitle}</p>
          </div>
        </div>
        <button
          onClick={exportCsv}
          disabled={loading || rows.length === 0}
          data-testid={`report-export-${def.key}`}
          className="rg-tap px-3 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wide rounded-sm flex items-center gap-2 text-xs border-2 border-slate-900"
        >
          <Download strokeWidth={3} className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 py-6 text-sm">Loading…</div>
      ) : error ? (
        <div className="text-rose-700 text-sm font-bold flex items-center gap-2 py-3">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-slate-500 italic text-sm py-6 text-center" data-testid={`report-empty-${def.key}`}>
          No data in this window.
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid={`report-table-${def.key}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200 bg-slate-50">
                {def.columns.map((c) => (
                  <th key={c.key} className="py-2 px-2.5 whitespace-nowrap">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                // Prefer a stable, unique row key. Each report exposes a natural primary key:
                //   daily-yard-summary    → day
                //   queue-performance     → stage
                //   crew-leader-performance → crew_leader
                //   project-station-summary → project_station
                //   low-stock-reorder     → item_name
                const rowKey =
                  r.day || r.stage || r.crew_leader || r.project_station || r.item_name || `${def.key}-${i}`;
                return (
                <tr
                  key={rowKey}
                  data-testid={`report-row-${def.key}-${i}`}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  {def.columns.map((c) => {
                    let val = r[c.key];
                    let cell;
                    if (c.key === "day" && val) cell = formatLocalDate(val);
                    else if (c.key === "stock_level" && val)
                      cell = <span className={`inline-block border-2 rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STOCK_PILL[val] || "bg-slate-100"}`}>{val}</span>;
                    else if (c.key === "stage" && val)
                      cell = <span className={`inline-block border-2 rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STAGE_PILL[val] || "bg-slate-100"}`}>{val}</span>;
                    else if (c.key === "completion_pct" && Number.isFinite(val))
                      cell = `${val.toFixed(1)}%`;
                    else cell = val ?? <span className="text-slate-400">—</span>;
                    return <td key={c.key} className="py-2 px-2.5 whitespace-nowrap">{cell}</td>;
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-section meta strip */}
      {!loading && rows.length > 0 && (def.key === "daily-yard-summary" || def.key === "low-stock-reorder") && (
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
          {def.key === "daily-yard-summary" && (
            <>
              <span><strong>{meta.total_received}</strong> received</span>
              <span><strong>{meta.total_completed}</strong> completed</span>
            </>
          )}
          {def.key === "low-stock-reorder" && (
            <>
              <span className="font-bold text-rose-700">{meta.red_count} red</span>
              <span className="font-bold text-amber-700">{meta.yellow_count} yellow</span>
            </>
          )}
        </div>
      )}
    </section>
  );
}
